import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Check, Download, FileText } from "lucide-react";
import { requestPitchDeck, pitchDeckDownloadUrl, formatApiError } from "@/lib/api";

const INVESTOR_TYPES = [
  "Local Investor",
  "International Investor",
  "Family Office",
  "Institutional",
  "Other",
];

export default function PitchDeckDialog({ open, onClose }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    role: "",
    investor_type: INVESTOR_TYPES[0],
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleClose = () => {
    if (submitting) return;
    onClose();
    setTimeout(() => {
      setDone(false);
      setError("");
    }, 350);
  };

  const triggerDownload = () => {
    const a = document.createElement("a");
    a.href = pitchDeckDownloadUrl;
    a.download = "U-NEXX-Reign-Villa-Resorts-Pitch.pdf";
    a.target = "_blank";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name || !form.email) {
      setError("Please provide your name and email.");
      return;
    }
    setSubmitting(true);
    try {
      await requestPitchDeck(form);
      setDone(true);
      // small delay so user sees success state, then trigger download
      setTimeout(triggerDownload, 600);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="pitch-dialog"
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            onClick={handleClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />
          <motion.div
            className="relative w-full max-w-3xl bg-[#12253D] border border-white/10 grid md:grid-cols-5 max-h-[90vh] overflow-hidden"
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.98 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          >
            <button
              type="button"
              data-testid="pitch-dialog-close"
              onClick={handleClose}
              className="absolute top-5 right-5 z-10 text-[#8FA3B8] hover:text-white transition"
              aria-label="Close"
            >
              <X size={20} />
            </button>

            {/* Left visual */}
            <div className="hidden md:flex md:col-span-2 bg-[#0A1628] border-r border-white/10 p-8 flex-col justify-between relative">
              <div>
                <div className="overline mb-6">Investor Pitch · 2026</div>
                <h3 className="font-serif-display text-3xl text-[#F3EFE4] leading-[1] mb-6">
                  A one-page<br />investor brief.
                </h3>
              </div>
              <div className="space-y-4 text-sm text-[#8FA3B8]">
                <div className="flex items-start gap-3">
                  <FileText size={16} className="text-[#D4AF37] mt-0.5 shrink-0" />
                  <span>Thesis, market snapshot, returns history.</span>
                </div>
                <div className="flex items-start gap-3">
                  <FileText size={16} className="text-[#D4AF37] mt-0.5 shrink-0" />
                  <span>Project case studies & ROI ranges.</span>
                </div>
                <div className="flex items-start gap-3">
                  <FileText size={16} className="text-[#D4AF37] mt-0.5 shrink-0" />
                  <span>How U-NEXX works with investor capital.</span>
                </div>
              </div>
              <div className="pt-8 mt-8 border-t border-white/10 text-xs text-[#8FA3B8]">
                Confidential. For prospective investors only.
              </div>
            </div>

            {/* Right form */}
            <div className="md:col-span-3 p-8 md:p-10 overflow-y-auto">
              {!done ? (
                <form onSubmit={handleSubmit}>
                  <div className="overline mb-3">Download · Free</div>
                  <h3 className="font-serif-display text-3xl md:text-4xl text-[#F3EFE4] mb-2">
                    Get the deck.
                  </h3>
                  <p className="text-[#8FA3B8] text-sm mb-7">
                    Tell us where to send your follow-up and we'll start the download immediately.
                  </p>

                  <div className="space-y-6">
                    <div>
                      <label className="field-label" htmlFor="pd-name">Full Name *</label>
                      <input id="pd-name" data-testid="pitch-input-name" className="field-input" value={form.name} onChange={update("name")} required />
                    </div>
                    <div>
                      <label className="field-label" htmlFor="pd-email">Email *</label>
                      <input id="pd-email" type="email" data-testid="pitch-input-email" className="field-input" value={form.email} onChange={update("email")} required />
                    </div>
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <label className="field-label" htmlFor="pd-company">Company</label>
                        <input id="pd-company" data-testid="pitch-input-company" className="field-input" value={form.company} onChange={update("company")} />
                      </div>
                      <div>
                        <label className="field-label" htmlFor="pd-role">Role</label>
                        <input id="pd-role" data-testid="pitch-input-role" className="field-input" value={form.role} onChange={update("role")} />
                      </div>
                    </div>
                    <div>
                      <label className="field-label" htmlFor="pd-type">Investor Profile</label>
                      <select id="pd-type" data-testid="pitch-input-investor-type" className="field-input" value={form.investor_type} onChange={update("investor_type")}>
                        {INVESTOR_TYPES.map((t) => (
                          <option key={t} value={t} className="bg-[#12253D]">{t}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {error && (
                    <div data-testid="pitch-error" className="mt-6 text-sm text-red-400 border border-red-500/30 px-4 py-3">
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    data-testid="pitch-submit"
                    disabled={submitting}
                    className="btn-primary mt-8 w-full justify-center disabled:opacity-50"
                  >
                    {submitting ? "Preparing your deck…" : "Download Pitch Deck"}
                    {!submitting && <Download size={16} />}
                  </button>

                  <p className="text-[10px] text-[#8FA3B8] mt-4 leading-relaxed">
                    By downloading you agree we may contact you about future opportunities. We never share your data.
                  </p>
                </form>
              ) : (
                <div data-testid="pitch-success" className="h-full flex flex-col items-center justify-center text-center py-8">
                  <div className="mx-auto mb-8 w-16 h-16 border border-[#D4AF37] flex items-center justify-center">
                    <Check className="text-[#D4AF37]" size={28} />
                  </div>
                  <div className="overline mb-3">Download started</div>
                  <h3 className="font-serif-display text-3xl md:text-4xl text-[#F3EFE4] mb-4">
                    Thank you.
                  </h3>
                  <p className="text-[#8FA3B8] mb-8 max-w-xs">
                    Your pitch deck is downloading now. We've logged your request and will follow up shortly.
                  </p>
                  <div className="flex gap-3">
                    <a
                      href={pitchDeckDownloadUrl}
                      data-testid="pitch-redownload"
                      target="_blank"
                      rel="noreferrer"
                      className="btn-secondary"
                    >
                      Download Again
                    </a>
                    <button data-testid="pitch-success-close" onClick={handleClose} className="btn-primary">
                      Close
                    </button>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
